import React, { Fragment } from "react";
import { Box, Button, DialogActions, DialogContent, DialogTitle, IconButton, List, ListItem, Typography } from "@mui/material";
import { useIsNotificationOpen, useNotification, useNotificationActions } from "../../store/notificationStore";
import CustomDialog from "./CustomDialog";
import { Check, CheckCircle, PauseCircle, X } from "@phosphor-icons/react";
import { useSuspension } from "../../hooks/apiHooks/suspensionHooks";
import { usePage } from "@inertiajs/react";
import { formatIsoDate } from "../../helpers/dateHelper";
import { capitalizeFirstLetter, formatSlugToTitle } from "../../helpers/stringHelper";

const NotificationDialog = () => {
  const notification = useNotification();
  const isOpen = useIsNotificationOpen();
  const { setIsOpen } = useNotificationActions();

  const handleClose = () => {
    setIsOpen(false);
  };

  const ModeComponentMap = {
    "account-suspended": {
      title: "Account Suspended",
      component: <AccountSuspended notification={notification} />,
      width: "730px",
    },
    "suspension-lifted": {
      title: "Suspension Lifted",
      component: <SuspensionLifted notification={notification} />,
      width: "680px",
    },
  };

  return (
    <CustomDialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          width: ModeComponentMap[notification.type]?.width,
        },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <DialogTitle sx={{ m: 0, p: 0 }}>
          <Typography
            sx={{
              fontFamily: "Inter",
              fontWeight: 700,
              fontSize: "24px",
              lineHeight: "100%",
              textAlign: "center",
              color: "primary.main",
            }}
          >
            {ModeComponentMap[notification.type]?.title || "Invalid Notification Type"}
          </Typography>
        </DialogTitle>
        <IconButton aria-label="close" onClick={handleClose} sx={{ color: (theme) => theme.palette.grey[500] }}>
          <X />
        </IconButton>
      </Box>

      {ModeComponentMap[notification.type]?.component ?? <Box p={3}>Invalid mode</Box>}
    </CustomDialog>
  );
};

export default NotificationDialog;

const AccountSuspended = ({ notification }) => {
  const { auth } = usePage().props;
  const { data } = useSuspension(notification?.meta?.suspension_id);

  return (
    <Fragment>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, mb: 3, mt: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              bgcolor: "#DEF2FC",
              color: "primary.main",
              borderRadius: 50,
              width: 140,
            }}
          >
            <PauseCircle size={140} weight="thin" />
          </Box>
          <Typography textAlign={"center"} fontSize={28} fontWeight={700} px={5} mt={1}>
            Your Account Has Been Suspended
          </Typography>
        </Box>

        <Box sx={{ mx: 5, display: "flex", flexDirection: "column", gap: 3.5 }}>
          <Typography fontSize="16px" lineHeight="27px">
            Your creator profile on WannaCollab has been temporarily suspended due to a violation of our platform guidelines.
          </Typography>

          <List sx={{ listStyleType: "disc", pl: 3, py: 0 }}>
            <ListItem sx={{ display: "list-item", pl: 0, py: 0 }}>
              Creator Profile:&nbsp;
              <Typography component="span" fontWeight={700} fontSize="16px" lineHeight="27px">
                {auth?.user?.name}
              </Typography>
            </ListItem>
            <ListItem sx={{ display: "list-item", pl: 0, py: 0 }}>
              Date of Suspension:&nbsp;
              <Typography component="span" fontWeight={700} fontSize="16px" lineHeight="27px">
                {formatIsoDate(data?.suspension.created_at)}
              </Typography>
            </ListItem>
            <ListItem sx={{ display: "list-item", pl: 0, py: 0 }}>
              Reason:&nbsp;
              <Typography component={"span"} fontWeight={700} fontSize="16px" lineHeight="27px">
                {formatSlugToTitle(data?.suspension.reason)}
              </Typography>
            </ListItem>
            <ListItem sx={{ display: "list-item", pl: 0, py: 0 }}>
              Duration:&nbsp;
              <Typography component={"span"} fontWeight={700} fontSize="16px" lineHeight="27px">
                {capitalizeFirstLetter(data?.suspension?.duration)}
              </Typography>
            </ListItem>
            <ListItem sx={{ display: "list-item", pl: 0, py: 0 }}>
              Limit:&nbsp;
              <Typography component={"span"} fontWeight={700} fontSize="16px" lineHeight="27px">
                {data?.suspension.meta.max_request} per day
              </Typography>
            </ListItem>
          </List>

          <Typography fontSize="16px" lineHeight="27px">
            During this suspension, you will be restricted to a maximum of&nbsp;
            <Typography component={"span"} fontWeight={700} fontSize="16px" lineHeight="27px">
              {data?.suspension.meta.max_request}
            </Typography>
            &nbsp;collaboration requests per day.
          </Typography>
          <Typography fontSize="16px" lineHeight="27px">
            If you believe this was a mistake or would like to appeal the suspension, you may contact our support team by
            clicking the button below:
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: "0 !important", pt: "16px !important" }}>
        <Box sx={{ display: "flex", width: "100%", gap: 2, justifyContent: "center" }}>
          <Button
            variant="contained"
            component="a"
            href="contact-us"
            target="_blank"
            sx={{ width: { xs: "100%", sm: "250px" }, color: "#FFF", my: 2 }}
            rel="noopener noreferrer"
          >
            Contact Us
          </Button>
        </Box>
      </DialogActions>
    </Fragment>
  );
};

const SuspensionLifted = () => {
  return (
    <Fragment>
      <DialogContent sx={{ zoom: "90%" }}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, mb: 3, mt: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              bgcolor: "#DEF2FC",
              color: "primary.main",
              borderRadius: 50,
              width: 140,
            }}
          >
            <CheckCircle size={140} weight="thin" />
          </Box>
          <Typography textAlign={"center"} fontSize={24} fontWeight={700} px={5} mt={1}>
            Your Account Suspension Has Been Lifted
          </Typography>
        </Box>

        <Box sx={{ mx: 5, display: "flex", flexDirection: "column", gap: 3.5 }}>
          <Typography fontSize="16px" lineHeight="27px">
            Your account suspension has been lifted, and all restrictions have been lifted. You may now send unlimited
            collaboration requests.
          </Typography>

          <Typography fontSize="16px" lineHeight="27px">
            We appreciate your cooperation and look forward to your continued contributions.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ width: "100%", padding: "0 !important", pt: "16px !important" }}>
        <Box sx={{ display: "flex", width: "100%", gap: 2, justifyContent: "center" }}>
          <Button variant="contained" sx={{ width: { xs: "100%", sm: "250px" }, color: "#FFF", my: 2 }}>
            Close
          </Button>
        </Box>
      </DialogActions>
    </Fragment>
  );
};
